import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Input, Button, Dropdown, DropdownOption } from '@column/column-ui-kit';
import styled from 'styled-components';
import {
  EditBox,
  EditSection,
  EditSectionTitle,
  EditPage,
  EditToolbar,
  EditTitle,
  Headline,
  FormElement,
  FormLabel,
} from '~/styles';
import { Breadcrumb, NotificationList, RestrictedBanner } from '~/components';
import { ROUTE } from '~/app/routes';
import { BankAccountRepository, CheckIssueRequest, CheckRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { generateFields, formatNumber } from '~/util';

export const RightArrow = styled(Icon.ArrowRight)`
  --icon-size: 16px;
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  position: absolute;
  top: 40px;
  right: -8px;
`;

export const SourceField = styled.div`
  position: relative;
  padding-right: 20px;
`;

export const PageTransfersIssue: React.FC = () => {
  const { currentUser, currentPermission } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState<Partial<CheckIssueRequest>>({});
  const [bankAccountList, setBankAccountList] = useState<DropdownOption[]>([]);
  const [idempotency, setIdempotency] = useState<string>('');

  const fetchBankAccounts = async (search?: string) => {
    let result: any = [];

    try {
      const response = await BankAccountRepository.getAll({ description: search });
      result = response.bankAccounts;
    } catch (error) {
      addDangerNotification({
        content: error as string,
        display: 'page',
      });
    }

    const entries: DropdownOption[] = [];

    result.map((entry: any) => {
      entries.push({
        label: `${entry.displayName || entry.description} (${formatNumber(entry?.balances?.availableAmount)})`,
        small: entry.id,
        value: entry.id,
      });
    });

    setBankAccountList(entries);
  };

  const handleSubmit = () => {
    if (!currentUser) {
      return;
    }

    CheckRepository.issue(transfer, idempotency)
      .then(() => {
        navigate(ROUTE.TRANSFERS);

        addSuccessNotification({
          content: 'Transfer added',
          display: 'page',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
          display: 'page',
        });
      });
    return;
  };

  const fetchData = () => {
    fetchBankAccounts(undefined);
  };

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    fetchData();
  }, []);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        () => {
          if (!currentUser) {
            return;
          }
          fetchData();
        },
        {
          fireImmediately: true,
        }
      ),
    []
  );

  return (
    <EditPage>
      <EditToolbar>
        <EditTitle>
          <Headline>Issue check</Headline>
          <Breadcrumb
            entries={[
              {
                label: (
                  <>
                    <Icon.Swap />
                    Transfers
                  </>
                ),
                path: ROUTE.TRANSFERS,
              },
              {
                label: 'Issue Check',
              },
            ]}
          />
        </EditTitle>
      </EditToolbar>
      <div>
        <NotificationList display="page" />
        <EditPage>
          {currentPermission?.issueChecks !== 'write' && <RestrictedBanner />}
          <EditSection>
            <EditSectionTitle>
              <Icon.CircleInfo />
              Information
            </EditSectionTitle>
            <EditBox data-disabled={currentPermission?.issueChecks !== 'write'}>
              {generateFields<CheckIssueRequest>(
                [
                  {
                    type: 'amount',
                    label: 'Amount',
                    value: 'positive_pay_amount',
                    newRow: true,
                    currencyCode: 'currencyCode',
                  },
                ],
                transfer,
                setTransfer
              )}
              <EditBox pale>
                <FormElement newRow>
                  <FormLabel>Bank Account ID</FormLabel>
                  <SourceField>
                    <Dropdown
                      options={bankAccountList}
                      active={transfer.bankAccountId}
                      fullWidth
                      search
                      searchLabel="Search for description"
                      onSearchChange={(value: string) => fetchBankAccounts(value)}
                      onChange={(value: string) => setTransfer({ ...transfer, bankAccountId: value })}
                    />
                  </SourceField>
                  <RightArrow />
                </FormElement>
                {generateFields<CheckIssueRequest>(
                  [
                    {
                      type: 'input',
                      label: 'Payee Name',
                      value: 'payee_name',
                    },
                  ],
                  transfer,
                  setTransfer
                )}
              </EditBox>
              {generateFields<CheckIssueRequest>(
                [
                  {
                    type: 'input',
                    label: 'Description',
                    value: 'description',
                    newRow: true,
                  },
                ],
                transfer,
                setTransfer
              )}
              <FormElement>
                <FormLabel>Idempotency Key</FormLabel>
                <Input onChange={setIdempotency} value={idempotency ?? ''} placeholder="Idempotency" />
              </FormElement>
            </EditBox>
          </EditSection>
        </EditPage>
      </div>
      <EditToolbar>
        <Button onClick={() => navigate(-1)} variant="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} isDisabled={currentPermission?.issueChecks !== 'write'}>
          Create
        </Button>
      </EditToolbar>
    </EditPage>
  );
};
